import Link from 'next/link';
import { handleFreeReturn } from '@lambda/commons/helpers';
import { SearchOffers } from '@lambda/commons/apis/elasticsearch/types';
import { Product } from '@lambda/apis/src/product/types';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import seo from '@/settings/seo';
import { EssentialSearchOffers } from '@/lib/collection-search-helpers';

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

function parseHTML(htmlString: string) {
  let formattedHtmlString = htmlString
    .replace(/<h2/g, `<h2 class="text-xl font-bold mt-8"`)
    .replace(/<h3/g, `<h3 class="font-bold mt-5"`)
    .replace(/<p/g, `<p class="text-sm mt-1"`)
    .replace(/<li/g, `<li class="text-sm mt-5"`)
    .replace(/<a/g, `<a class="underline text-blue-500"`)
    .replace(/{{country}}/g, settings.country)
    .replace(/Reebelo/g, `<a href="/">Reebelo</a>`);

  if (settings.store === 'quista') {
    formattedHtmlString = formattedHtmlString.replace(
      /12\s*-?\s*months?/gi,
      'up to 12 months',
    );
    formattedHtmlString = formattedHtmlString.replace(
      /a up to 12 months warranty/gi,
      'an up to 12 months warranty',
    );
  }

  return formattedHtmlString;
}

const t = i18n.init();
interface SeoSectionProps {
  pageTitle?: string;
  textTitle?: string;
  offers?: SearchOffers[] | EssentialSearchOffers[];
  handle?: string;
  products?: Record<string, Product>;
}

export default function SeoSection(props: SeoSectionProps) {
  const {
    pageTitle = '',
    offers = [],
    products = {},
    textTitle = '',
    handle: mainHandle = '',
  } = props;
  const isUSStore = settings.store === 'reebelo-us';

  const handles = offers.reduce<{ handle: string; title: string }[]>(
    (acc, offer) => {
      const { psku } = offer;

      if (psku && psku in products) {
        const { handle, title } = products[psku];

        acc.push({ handle, title });
      }

      return acc;
    },
    [],
  );

  if (mainHandle && seo[mainHandle]) {
    return (
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: parseHTML(seo[mainHandle]),
        }}
      ></div>
    );
  }

  if (pageTitle) {
    const Links = handles
      .map((handle, index) => ({
        ...handle,
        isLast: index === handles.length - 1,
      }))
      .map((collection) => (
        <Link
          href={`/collections/${collection.handle}`}
          key={collection.handle}
        >
          <a className="text-blue-500">
            {t(collection.title)}
            {collection.isLast ? '' : ', '}
          </a>
        </Link>
      ));

    return (
      <div>
        <h2 className="mb-2 mt-8 text-base font-semibold !leading-5 text-gray-700 xxs:mb-3 xxs:text-lg xxs:!leading-6 xs:text-xl sm:!leading-normal md:mt-10 md:text-2xl">
          {t`Buy Refurbished`} {t(pageTitle)}{' '}
          {isUSStore
            ? 'in the United States'
            : t`in ${{
                country: settings.country,
              }}`}
        </h2>
        <div className="text-sm text-gray-700 xs:text-base">
          <p>
            {t`Looking to buy a refurbished`} {t(textTitle)}
            {isUSStore
              ? ''
              : t`? Or do you prefer a brand
          new`}
            {!isUSStore && ' '}
            {!isUSStore && t(textTitle)}
            {t`? In any case, you’re at the right place! Whether you are
          looking for the latest`}{' '}
            {t(textTitle)} {t`or one of the previous`} {t(textTitle)}{' '}
            {t`models, we’ve got you covered. Save big by getting a second
          hand `}
            {t(textTitle)} {t`such as `}
            {Links} {t`at`}
            {` `}
            <Link href="/">
              <a className="text-blue-500">Reebelo</a>
            </Link>
            {t`. All our`} {t(textTitle)} {t`come with up to`}{' '}
            {settings.warranty_month}{' '}
            {t`months warranty, ${{
              free_return_days: freeReturnDays,
            }} days free returns and free shipping.`}
          </p>
        </div>
      </div>
    );
  }

  return null;
}
